// Simulateur de salaire brut/net — les taux proviennent du Regulatory Engine (GET /api/public/regulatory/bareme).
// DEFAULT_BAREME = valeurs de repli 2026 (identiques au seed serveur) si l'API est indisponible.
// ⚠️ Calculs simplifiés — pour information uniquement.

export const DEFAULT_BAREME = {
  pass_mensuel: 3925,
  smic_horaire: 11.88,
  heures_mensuelles: 151.67,
  csg_abattement: 98.25,
  vieillesse_plaf: { sal: 6.9, pat: 8.55, base: "plafond" },
  vieillesse_deplaf: { sal: 0.4, pat: 2.02, base: "brut" },
  retraite_t1: { sal: 3.15, pat: 4.72, base: "plafond" },
  ceg_t1: { sal: 0.86, pat: 1.29, base: "plafond" },
  cet: { sal: 0.14, pat: 0.21, base: "brut" },
  apec: { sal: 0.024, pat: 0.036, base: "plafond" },
  csg_deductible: { sal: 6.8, pat: 0, base: "csg" },
  csg_non_deductible: { sal: 2.4, pat: 0, base: "csg" },
  crds: { sal: 0.5, pat: 0, base: "csg" },
  ir_tranches: [
    { up_to: 11497, rate: 0 },
    { up_to: 29315, rate: 11 },
    { up_to: 83823, rate: 30 },
    { up_to: 180294, rate: 41 },
    { up_to: null, rate: 45 },
  ],
  patronal_global_non_cadre: 25,
  patronal_global_cadre: 30,
};

const sal = (c) => (typeof c === "object" && c ? Number(c.sal) || 0 : Number(c) || 0) / 100;

export function simulerSalaire({ brut_mensuel, statut = "non_cadre", parts = 1, bareme }) {
  const b = { ...DEFAULT_BAREME, ...(bareme || {}) };
  const brut = Math.max(0, parseFloat(brut_mensuel) || 0);
  const pass = Number(b.pass_mensuel) || DEFAULT_BAREME.pass_mensuel;
  const base_plaf = Math.min(brut, pass);
  const csg_base = brut * ((Number(b.csg_abattement) || 98.25) / 100);

  const lines = [
    { key: "vieillesse_plaf", label: "Vieillesse plafonnée", rate: sal(b.vieillesse_plaf), base: base_plaf },
    { key: "vieillesse_deplaf", label: "Vieillesse déplafonnée", rate: sal(b.vieillesse_deplaf), base: brut },
    { key: "retraite_t1", label: "Retraite complémentaire AGIRC-ARRCO T1", rate: sal(b.retraite_t1), base: base_plaf },
    { key: "ceg_t1", label: "Contribution d'équilibre général (CEG)", rate: sal(b.ceg_t1), base: base_plaf },
    { key: "cet", label: "Contribution d'équilibre technique (CET)", rate: sal(b.cet), base: brut },
    ...(statut === "cadre" ? [{ key: "apec", label: "APEC", rate: sal(b.apec), base: base_plaf }] : []),
    { key: "csg_deductible", label: "CSG déductible", rate: sal(b.csg_deductible), base: csg_base },
    { key: "csg_non_deductible", label: "CSG non déductible", rate: sal(b.csg_non_deductible), base: csg_base },
    { key: "crds", label: "CRDS", rate: sal(b.crds), base: csg_base },
  ].map((l) => ({ ...l, montant: +(l.base * l.rate).toFixed(2) }));

  const total_cotisations_salariales = lines.reduce((s, l) => s + l.montant, 0);
  // CSG non déductible + CRDS sont réintégrées dans le net imposable
  const reintegre = lines.filter((l) => l.key === "csg_non_deductible" || l.key === "crds").reduce((s, l) => s + l.montant, 0);

  const net_avant_impot = +(brut - total_cotisations_salariales).toFixed(2);
  const net_imposable = +(net_avant_impot + reintegre).toFixed(2);

  // IR mensuel approximatif : barème annuel par part ramené au mois
  const tranches = (Array.isArray(b.ir_tranches) && b.ir_tranches.length ? b.ir_tranches : DEFAULT_BAREME.ir_tranches)
    .map((t) => ({ up_to: t.up_to == null ? Infinity : Number(t.up_to) / 12, rate: (Number(t.rate) || 0) / 100 }));
  const revenu_par_part = net_imposable / parts;
  let ir = 0;
  let prev = 0;
  for (const t of tranches) {
    if (revenu_par_part > prev) {
      const ceiling = Math.min(revenu_par_part, t.up_to);
      ir += (ceiling - prev) * t.rate;
      prev = ceiling;
    }
  }
  ir = +(ir * parts).toFixed(2);
  const net_apres_ir = +(net_avant_impot - ir).toFixed(2);

  const patronal = (Number(statut === "cadre" ? b.patronal_global_cadre : b.patronal_global_non_cadre) || 25) / 100;
  const cout_employeur = +(brut * (1 + patronal)).toFixed(2);
  const charges_patronales = +(cout_employeur - brut).toFixed(2);
  const smic_mensuel = (Number(b.smic_horaire) || 11.88) * (Number(b.heures_mensuelles) || 151.67);

  return {
    brut: +brut.toFixed(2),
    statut,
    parts,
    lines,
    total_cotisations_salariales: +total_cotisations_salariales.toFixed(2),
    net_avant_impot,
    net_imposable,
    impot_revenu_mensuel: ir,
    net_apres_ir,
    cout_employeur,
    charges_patronales,
    smic_mensuel: +smic_mensuel.toFixed(2),
    smic_ratio: +(brut / smic_mensuel).toFixed(2),
  };
}

export function fmtEUR(n) {
  if (n == null) return "—";
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}
